import { BarChart3, CalendarDays, ClipboardList, Compass, FileImage, ImagePlus, Layers, Library, LogOut, Plus, PlusCircle, Quote, Settings as SettingsIcon, Sparkles, Video, X } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";
import type { Route } from "../App";
import type { ChannelFeatures } from "../api";
import { logout } from "../auth";
import { emitAuthExpired } from "../auth";
import { hasDraft } from "../lib/brandSetup/draft";

interface Props {
  active: Route;
  /** Multi-channel feature flag. False → render the original V1 nav. */
  channelsEnabled: boolean;
  /** Active channel's enabled pipelines — gates Posts/Originals nav items. */
  features: ChannelFeatures;
  /** Catalog video jobs currently rendering (drives the queue chip). */
  activeJobCount: number;
  /** Beta post drafts currently re-rendering slides server-side. */
  activeBetaPostCount: number;
  /** Sum of all in-flight work — drives the bottom "live" widget. */
  activeLiveCount: number;
  /** Scanned scenes still waiting on the shelf (active channel) — Backlog count badge. */
  backlogSceneCount: number;
  /** A scan/locate is streaming → the Backlog badge shows a live spinner. */
  backlogScanning: boolean;
  onNav: (r: Route) => void;
  /** "drawer" renders the sidebar to fill an off-canvas mobile drawer
   *  (full width/height, not sticky). Defaults to the desktop rail. */
  variant?: "desktop" | "drawer";
  /** When provided (drawer mode), shows a close (✕) button in the header. */
  onClose?: () => void;
  /** Jump to Brand Setup (or the classic wizard). Shown as a nav item only
   *  when provided — the desktop rail already has this entry point on
   *  `ChannelSwitcher`, so only the mobile drawer's `Sidebar` gets one
   *  (spec §2.6: Brand Setup must be reachable on a phone at all). */
  onNavigateOnboard?: () => void;
}

async function signOut(): Promise<void> {
  await logout();
  emitAuthExpired();
}

type NavItem = {
  id: Route;
  label: string;
  icon: typeof PlusCircle;
  /** Only show when this channel feature is enabled. Omitted = always shown. */
  feature?: keyof ChannelFeatures;
  /** Render disabled with a "Soon" chip (feature gated but not shipped yet). */
  comingSoon?: boolean;
};

// Channel-aware nav (feature-gated, Originals "Soon" chip, Quotes merged
// into Inspiration). Used only when CHANNELS_ENABLED.
const CHANNEL_ITEMS: NavItem[] = [
  { id: "new", label: "New job", icon: PlusCircle },
  { id: "backlog", label: "Backlog", icon: ClipboardList },
  { id: "queue", label: "Render queue", icon: Layers },
  { id: "library-videos", label: "Library Videos", icon: Library },
  { id: "library-posts", label: "Library Posts", icon: FileImage, feature: "posts" },
  { id: "originals", label: "Originals", icon: Sparkles, feature: "originals", comingSoon: true },
  { id: "inspiration", label: "Inspiration", icon: ImagePlus },
  { id: "sources", label: "Sources", icon: Video },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "cost", label: "Usage & cost", icon: BarChart3 },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "guide", label: "Guide", icon: Compass },
];

// The guided tour's spotlight targets on the rail (spec §2.6). Present in
// both nav variants since the item ids are shared.
const NAV_TOUR_ANCHOR: Partial<Record<Route, string>> = {
  new: "nav-new",
  queue: "nav-queue",
  "library-videos": "nav-library",
  guide: "nav-guide",
};

/** One row of the rail — shared by the nav items and the Guide entry so the
 *  tour's row is indistinguishable from its neighbours. */
function navRowStyle(isActive: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: "none",
    color: isActive ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
    background: isActive ? "rgba(255,213,0,.10)" : "transparent",
    border: isActive ? "1px solid rgba(255,213,0,.20)" : "1px solid transparent",
    boxShadow: isActive ? "0 0 16px rgba(255,213,0,.10)" : "none",
    transition: "all .2s var(--ql-ease)",
  };
}

// Original single-brand V1 nav — standalone Library Quotes, no Originals,
// no feature gating. Used when CHANNELS_ENABLED is off.
const V1_ITEMS: NavItem[] = [
  { id: "new", label: "New job", icon: PlusCircle },
  { id: "backlog", label: "Backlog", icon: ClipboardList },
  { id: "queue", label: "Render queue", icon: Layers },
  { id: "library-videos", label: "Library Videos", icon: Library },
  { id: "library-posts", label: "Library Posts", icon: FileImage },
  { id: "library-quotes", label: "Library Quotes", icon: Quote },
  { id: "inspiration", label: "Inspiration", icon: ImagePlus },
  { id: "sources", label: "Sources", icon: Video },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "cost", label: "Usage & cost", icon: BarChart3 },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "guide", label: "Guide", icon: Compass },
];

export function Sidebar({
  active,
  channelsEnabled,
  features,
  activeJobCount,
  activeBetaPostCount,
  activeLiveCount,
  backlogSceneCount,
  backlogScanning,
  onNav,
  variant = "desktop",
  onClose,
  onNavigateOnboard,
}: Props): React.JSX.Element {
  const isDrawer = variant === "drawer";
  const items = channelsEnabled
    ? CHANNEL_ITEMS.filter((it) => !it.feature || features[it.feature])
    : V1_ITEMS;
  // "Continue setup" vs "Set up a brand" (spec §2.6) — re-checked whenever
  // the active route changes, since this component stays mounted across
  // shell-internal navigation (unlike Brand Setup itself, which is a focus-
  // mode screen outside the shell entirely).
  const [draftExists, setDraftExists] = useState(false);
  useEffect(() => {
    if (!onNavigateOnboard) return;
    try {
      setDraftExists(hasDraft(localStorage) !== null);
    } catch {
      setDraftExists(false);
    }
  }, [active, onNavigateOnboard]);
  return (
    <aside
      style={{
        width: isDrawer ? "100%" : 248,
        flexShrink: 0,
        height: isDrawer ? "auto" : "100vh",
        minHeight: isDrawer ? "100%" : undefined,
        position: isDrawer ? "static" : "sticky",
        top: 0,
        background: "rgba(8,9,14,.92)",
        borderRight: isDrawer ? "none" : "1px solid rgba(255,255,255,.06)",
        padding: "20px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          padding: "8px 8px 18px",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          position: onClose ? "relative" : undefined,
        }}
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 34,
              height: 34,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,.08)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg-muted)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        )}
        <img
          src="/logo-quotologies.png"
          alt="Quotologies"
          style={{
            width: 200,
            height: "auto",
            display: "block",
            filter: "drop-shadow(0 0 14px rgba(255,213,0,.18))",
          }}
        />
        <span
          style={{
            fontWeight: 700,
            fontSize: 11,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: "0.18em",
          }}
        >
          A.I. Editor
        </span>
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {items.map((it) => {
          const isActive = active === it.id;
          const Icon = it.icon;
          if (it.comingSoon) {
            return (
              <div
                key={it.id}
                title="Coming soon"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--ql-fg-subtle)",
                  border: "1px solid transparent",
                  cursor: "default",
                  opacity: 0.55,
                }}
              >
                <Icon size={18} strokeWidth={1.75} />
                {it.label}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,.06)",
                    color: "var(--ql-fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: ".05em",
                  }}
                >
                  Soon
                </span>
              </div>
            );
          }
          return (
            <a
              key={it.id}
              href="#"
              data-tour={NAV_TOUR_ANCHOR[it.id]}
              onClick={(e) => {
                e.preventDefault();
                onNav(it.id);
              }}
              style={navRowStyle(isActive)}
            >
              <Icon size={18} strokeWidth={1.75} />
              {it.label}
              {it.id === "queue" && activeJobCount > 0 && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(255,213,0,.18)",
                    color: "var(--ql-yellow)",
                  }}
                >
                  {activeJobCount}
                </span>
              )}
              {it.id === "library-posts" && activeBetaPostCount > 0 && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(123,160,255,.18)",
                    color: "#7BA0FF",
                  }}
                  title={`${activeBetaPostCount} post${activeBetaPostCount === 1 ? "" : "s"} re-rendering`}
                >
                  {activeBetaPostCount}
                </span>
              )}
              {it.id === "backlog" && (backlogSceneCount > 0 || backlogScanning) && (
                <span
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(255,213,0,.18)",
                    color: "var(--ql-yellow)",
                  }}
                  title={
                    backlogScanning
                      ? "Finding scenes…"
                      : `${backlogSceneCount} scanned scene${backlogSceneCount === 1 ? "" : "s"} waiting to render`
                  }
                >
                  {backlogScanning && (
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        border: "1.6px solid var(--ql-yellow)",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "ql-spin 0.8s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {backlogSceneCount > 0 && backlogSceneCount}
                </span>
              )}
            </a>
          );
        })}
        {onNavigateOnboard && (
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); onNavigateOnboard(); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
              color: "var(--ql-fg-muted)",
              background: "transparent",
              border: "1px solid transparent",
              transition: "all .2s var(--ql-ease)",
            }}
          >
            <Plus size={18} strokeWidth={1.75} />
            {draftExists ? "Continue setup" : "Set up a brand"}
          </a>
        )}
      </nav>

      <div
        style={{
          marginTop: "auto",
          padding: 14,
          borderRadius: 14,
          background: "linear-gradient(135deg,rgba(255,213,0,.08),rgba(16,18,27,.7))",
          border: "1px solid rgba(255,213,0,.18)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Local
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            fontFamily: "var(--ql-font-mono)",
            color: "var(--ql-yellow)",
          }}
        >
          {activeLiveCount} live
        </div>
        <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", marginTop: 2 }}>
          {(() => {
            if (activeLiveCount === 0) return "queue idle";
            // Surface the mix when both kinds of work are running.
            const parts: string[] = [];
            if (activeJobCount > 0) parts.push(`${activeJobCount} video${activeJobCount === 1 ? "" : "s"}`);
            if (activeBetaPostCount > 0) parts.push(`${activeBetaPostCount} post${activeBetaPostCount === 1 ? "" : "s"}`);
            return parts.join(" · ");
          })()}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 6px",
          borderTop: "1px solid rgba(255,255,255,.06)",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            background: "linear-gradient(135deg,#FFD500,#FFA500)",
            color: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          T
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Tamer B.</div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>Solo · Local</div>
        </div>
        <button
          type="button"
          onClick={() => { void signOut(); }}
          aria-label="Sign out"
          title="Sign out"
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,.08)",
            background: "rgba(255,255,255,.02)",
            color: "var(--ql-fg-muted)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all .15s var(--ql-ease)",
          }}
        >
          <LogOut size={15} strokeWidth={1.85} />
        </button>
      </div>
    </aside>
  );
}
